import { api } from './api.ts';
import { MSG } from './constants.ts';
import { env } from './env.ts';
import { cipher, getSessionToken } from './session.ts';
import type { DaoApiPagination, DaoApiResponse, DaoCampaign, DaoReward } from '@/types/dao.ts';

/**
 * 영상 시청 보상 API.
 *
 * `/api/user/*` 와 같은 규약으로 본문 전체를 AES-GCM 으로 감싼다.
 * 백엔드는 `ControllerApiCampaign`.
 */

/**
 * 미션 목록 (미션 화면).
 *
 * 백엔드 `ControllerApiCampaign::items()`. 공개(`F_OPEN='Y'`) · 사용중
 * (`F_STATUS='Y'`) 이면서 **오늘이 시작~종료일 사이**인 미션만 내려온다.
 * 기간이 지난 미션은 빠지므로 목록이 비어 있을 수 있다.
 *
 * ★ **형식(`type`)은 서버에서 거른다.** 예전에는 전체를 10건씩 받아 화면에서 다시
 *   걸렀는데, 그러면 탭마다 개수가 들쭉날쭉하고(1페이지에 음악이 4건뿐인데 실제로는
 *   6건) 나머지는 2페이지에 묻혀 영영 안 보였다. 페이지 수도 탭과 무관한 값이었다.
 *
 * @param type `M` 음악 / `V` 영상. 비우면 전부
 */
export async function fetchCampaignItems(
  page = 1,
  type?: string
): Promise<DaoApiResponse<DaoApiPagination<DaoCampaign>>> {
  const encrypted = await cipher.encryptJson({ p: page, type: type ?? '' });
  return api.post<DaoApiResponse<DaoApiPagination<DaoCampaign>>>(
    '/api/campaign/items.json',
    encrypted
  );
}

/** 캠페인 한 건 */
export async function fetchCampaign(seq: number): Promise<DaoApiResponse<DaoCampaign>> {
  const encrypted = await cipher.encryptJson({ id: seq });
  return api.post<DaoApiResponse<DaoCampaign>>('/api/campaign/data.json', encrypted);
}

/**
 * 음원 내려받기 (음악 미션).
 *
 * **왜 `<audio src="...">` 로 끝내지 않는가.**
 *   mp3 는 웹으로 열리지 않는 자리(`Storage/private/audio/`)에 있고, 응답에도
 *   경로가 실리지 않는다. 이 호출만 바이트를 준다. 받은 바이트로 Blob URL 을
 *   만들어 `<audio>` 에 물리면, 주소창에도 개발자도구 링크에도 원본 주소가
 *   남지 않는다.
 *
 * ★ 이것은 **DRM 이 아니다.** 재생할 수 있으면 저장도 할 수 있다는 사실은 변하지
 *   않는다. 여기서 막는 것은 '주소를 퍼 나르는 것' 까지다.
 *
 * ★ `api.post` 를 쓰지 않는다. 그쪽은 응답을 JSON/텍스트로 읽어서 바이너리가 깨진다.
 *
 * @throws Error 서버가 거절하면 그 메시지를 그대로 던진다.
 */
export async function fetchCampaignAudio(
  userSeq: number | undefined,
  campaignId: string | number,
  sessionid: string | null,
  ticket: string | null | undefined,
  signal?: AbortSignal
): Promise<Blob> {
  const encrypted = await cipher.encryptJson({ userSeq, id: campaignId, sessionid, ticket });

  const token = getSessionToken();
  const headers = new Headers({ 'Content-Type': 'application/json' });
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const response = await fetch(`${env.apiBase}/api/campaign/audio.mp3`, {
    method: 'POST',
    headers,
    body: JSON.stringify(encrypted),
    credentials: 'include',
    signal,
  });

  if (!response.ok) {
    // 거절은 JSON 으로 온다. 못 읽으면 일반 메시지로 넘긴다.
    const body = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new Error(body?.message || MSG.apiFail);
  }

  return response.blob();
}

export interface PrepareData {
  campaign: DaoCampaign;
  reward: DaoReward;
  /**
   * 다시 참여할 수 있는 시각 (`Y-m-d H:i:s`). 지금 받을 수 있으면 null.
   * 재참여 주기가 미션마다 다르므로 서버가 계산해서 내려준다.
   */
  /**
   * 음원을 받을 **한 번만 쓰는 티켓**. 음악 미션일 때만 온다.
   *
   * 로그인 토큰만으로는 로그인한 사람이 미션 번호를 바꿔가며 전량을 긁는 것을
   * 막지 못한다. 티켓을 끼우면 한 곡마다 `prepare` 를 먼저 불러야 하고, 그 호출이
   * 기록으로 남는다.
   */
  audioTicket?: string | null;

  nextRewardAt?: string | null;

  /**
   * 이 미션의 **총예산이 바닥났는가.**
   *
   * 총예산이 0인 미션도 여기 해당한다. 관리자가 예산을 비워 두면 지급합계와
   * 총예산이 둘 다 0이라 처음부터 소진 상태다.
   *
   * 재생을 막는 값이 **아니다.** 들을지 말지는 회원이 정한다.
   */
  soldOut?: boolean;
}

/**
 * 시청 준비. 서버가 대기 상태('H')의 리워드 행을 만들고 캠페인과 함께 돌려준다.
 * `sessionid` 는 이후 videoplayback 호출과 짝을 맞추는 키다.
 */
export async function prepareCampaign(
  userSeq: number | undefined,
  campaignId: string,
  sessionid: string | null
): Promise<DaoApiResponse<PrepareData>> {
  const encrypted = await cipher.encryptJson({ userSeq, id: campaignId, sessionid });
  return api.post<DaoApiResponse<PrepareData>>('/api/campaign/prepare.json', encrypted);
}

export interface PlaybackResult {
  reward: DaoReward;
  /** 재생이 끝났거나 중단돼 서버가 판정을 마쳤는지 */
  ended: boolean;
  /**
   * 포인트가 실제로 적립됐는지.
   *
   * ★ 보고(`videoplayback`)로는 **더 이상 적립되지 않는다.** (2026-09-16)
   *   적립은 회원이 '적립 받기' 를 눌러 `claimReward()` 를 부를 때만 일어난다.
   */
  saved: boolean;
  /**
   * 지금 '적립 받기' 를 누르면 받을 수 있는가. 버튼을 열고 닫는 값이다.
   *
   * 서버가 실제 지급 때와 **같은 검증**으로 계산한다. 재생이 끝나기 전에 열릴 수도
   * 있다 — 기준 시간이 매체 길이보다 짧게 설정된 미션이 있다.
   */
  claimable?: boolean;
  /**
   * 적립 금액. 백엔드 `ModelUserReward::amount` 가 `?string` 이라 문자열로 온다.
   * 숫자로 다뤄야 하면 반드시 Number() 로 감쌀 것.
   */
  amount: number | string;
}

export interface PlaybackRequest {
  rewardSeq?: number;
  campaignSeq?: number;
  userSeq?: number;
  sessionid: string | null;
  /** 현재 재생 위치(초) */
  current: number;
  /** 영상 전체 길이(초) */
  duration: number;
  /** 재생 중이면 true. false 면 서버가 종료로 보고 판정한다. */
  playing: boolean;
  /** 중단 사유. 값이 있으면 서버가 적립을 취소한다. */
  reason?: string | null;
}

/**
 * 재생 상태 보고 (5초 간격 + 시작/종료).
 *
 * ★ 서버가 이 호출 횟수와 시작~종료 경과시간으로 실제 시청을 검증한다.
 *   (`videoplayback()`: 경과시간 >= 영상길이-3초, 통신횟수 ≈ ceil(길이/5)+2 ±2)
 *   주기를 바꾸면 백엔드 검증식도 같이 바꿔야 한다.
 */
/**
 * 적립 받기.
 *
 * 재생 보고와 같은 값을 보낸다. 서버가 보고 기록으로 **다시 판정하고**, 통과하면
 * 그때 포인트를 넣는다(`ControllerApiCampaign::claim`). 화면이 버튼을 열어 줬다는
 * 것만으로 지급되지는 않는다.
 */
export async function claimReward(
  payload: PlaybackRequest
): Promise<DaoApiResponse<PlaybackResult | boolean>> {
  const encrypted = await cipher.encryptJson({ ...payload });
  return api.post<DaoApiResponse<PlaybackResult | boolean>>('/api/campaign/claim.json', encrypted);
}

export async function reportPlayback(
  payload: PlaybackRequest,
  /**
   * 페이지를 떠나는 순간에도 끝까지 보내야 할 때.
   *
   * 브라우저는 언로드 중에 시작된 보통의 요청을 끊어 버린다. `keepalive` 를 켜면
   * 문서가 사라져도 전송을 마친다(본문 64KB 제한 — 우리 보고는 그보다 훨씬 작다).
   */
  keepalive = false
): Promise<DaoApiResponse<PlaybackResult | boolean>> {
  const encrypted = await cipher.encryptJson({ ...payload });
  return api.post<DaoApiResponse<PlaybackResult | boolean>>(
    '/api/campaign/videoplayback.json',
    encrypted,
    keepalive ? { keepalive: true } : undefined
  );
}
